import { cn } from '@/lib/utils';
import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from './vital-codes';
import type { VitalCatalogEntry } from './vital-catalog';
import { computedVitalReading } from './vital-derive';
import { LmpEditor } from './lmp-editor';
import {
  extractVitalSeries,
  extractBmiSeries,
} from './vital-sparkline';
import {
  Sparkline,
  HearingTestEditor,
  HearingTestReadout,
  parseHearingValue,
  combineHearingValue,
  VisualAcuityEditor,
  VisualAcuityReadout,
  VitalAbnormalIndicator,
  computeVitalAbnormalTone,
} from '@scripsteam/scrips-signal-ds';
import {
  ALLOWED_NUMERATOR,
  ALLOWED_DENOMINATOR,
} from '@/encounter/lib/visual-acuity';

/**
 * VitalCell — single vital reading. Supports two modes:
 *   - **Read** (PR 1): displays the server-returned value with the
 *     abnormal pill when `vitalNormalStatus === 'abnormal'`.
 *   - **Edit** (PR 2 — this commit): numeric/combo cells become inline
 *     inputs bound to the parent's form state. Computed cells (BMI,
 *     waist-to-hip ratio) stay read-only — backend computes those.
 *     Special cells (Visual Acuity, Hearing, LMP) show a "Edit in
 *     PR 3" hint — those need bespoke editors that ship in PR 3.
 *
 * PATIENT-SAFETY NOTE — abnormal-flag source of truth:
 * The `Abnormal` pill renders from the server-returned
 * `vitalNormalStatus` field. The cell does ZERO client-side threshold
 * computation. Flutter parity (`vital_cell_widget.dart:358` →
 * `vital_data.dart:162-168`) is the same — `abnormalValue()` is a
 * pure read of `vitalAbnormalPercentage`. PR 2 ships
 * `vitalNormalStatus: null` on update so the server is the only
 * authority that flags a reading as abnormal — never the frontend.
 *
 * Tinted-pill contrast (CLAUDE.md §Tinted-pill rule): bg → /100, fill
 * → /500, text → /700. Same shade on text + bg is a documented bug.
 *
 * BP combo: when this cell is for SYSTOLIC (`8480-6`), it renders
 * "120 / 80" in read mode and two inputs side-by-side in edit mode.
 * The two readings remain discrete wire entries — each with its own
 * `vitalNormalStatus` / `vitalAbnormalPercentage` evaluated
 * independently by the server. Mirrors Flutter
 * `VitalUiData.items = [systolic, diastolic]`.
 */

interface VitalCellProps {
  entry: VitalCatalogEntry;
  /** The reading for THIS cell's code, or null if the encounter has
   *  no recorded value for this code yet. */
  reading: PatientVital | null;
  /** Full array — used to resolve combo cells (BP needs both codes). */
  allReadings: PatientVital[];
  /**
   * Compact inputs — a fixed narrow box instead of the full-width field (Flutter's pre-encounter
   * cell, Andrew 2026-08-09). Opt-in so the appointment panel keeps its own width.
   */
  compact?: boolean;
  /**
   * Suppress this cell's unit suffix. Flutter's pre-encounter cell prints a unit only where it
   * is a short symbol (°C · % · cm · kg) and omits the abbreviations (BPM · mmHg · RPM), even
   * though the model carries them (ui_data.dart:1184+). The CALLER decides, so this component
   * bakes no per-vital display policy.
   */
  hideUnit?: boolean;
  /** Hearing control geometry — see HearingEditor. */
  hearingVariant?: 'pills' | 'bar';
  /** When true, render input fields for editable cells. */
  editing?: boolean;
  /** Working form values keyed by code. Required when `editing`. */
  formValues?: Record<string, string>;
  /** Update callback fired on every keystroke. Required when `editing`. */
  onValueChange?: (code: string, value: string) => void;
  /**
   * LMP cell only: working copy of `PatientVital.isMenstrual`. Required
   * when the LMP cell renders in edit mode. Ignored for every other
   * cell.
   */
  lmpIsMenstrual?: boolean;
  /** LMP cell only: setter for the menstruating toggle. */
  onLmpMenstrualChange?: (next: boolean) => void;
  /** LMP cell only: render the three indicator rows (see LmpEditor.showIndicators). */
  lmpShowIndicators?: boolean;
  lmpIsBreastFeeding?: boolean;
  onLmpBreastFeedingChange?: (next: boolean) => void;
  lmpIsPregnant?: boolean;
  onLmpPregnantChange?: (next: boolean) => void;
  /**
   * Patient-wide historical readings used for the sparkline trend
   * chart. Optional — when not supplied, no sparkline renders.
   * The cell extracts its own per-code series via
   * `extractVitalSeries` (or `extractBmiSeries` for the BMI cell).
   */
  historicalReadings?: PatientVital[];
}

const EMPTY_VALUE = '—';

function findReading(
  readings: PatientVital[],
  code: string,
): PatientVital | null {
  return readings.find((r) => r.code?.code === code) ?? null;
}

function formatPrimaryValue(reading: PatientVital | null): string {
  const raw = reading?.quantity?.value;
  if (raw == null || raw.trim() === '') return EMPTY_VALUE;
  return raw;
}

/**
 * True when a cell has a reading to show: a non-blank value, or — for the BP combo cell, keyed on
 * systolic — a non-blank diastolic. Exactly the rule the cell inks itself by (`hasValue` below),
 * exported so a host that hides empty tiles hides exactly the ones that would print "—".
 */
export function vitalCellHasValue(
  entry: Pick<VitalCatalogEntry, 'code'>,
  readingIn: PatientVital | null,
  allReadings: PatientVital[],
): boolean {
  // A computed vital with no stored reading can still show a derived one (vitalsComputedLive).
  const reading = computedVitalReading(entry.code, readingIn, allReadings);
  if (formatPrimaryValue(reading) !== EMPTY_VALUE) return true;
  return (
    entry.code === VITAL_CODES.SYSTOLIC &&
    formatPrimaryValue(findReading(allReadings, VITAL_CODES.DIASTOLIC)) !== EMPTY_VALUE
  );
}

/**
 * Build the input-pattern regex based on the catalog entry's allow
 * flags. Empty string is always permitted (clearing the input).
 */
function inputPattern(entry: VitalCatalogEntry): RegExp {
  const decimal = entry.allowDecimal !== false; // default true
  const negative = entry.allowNegative === true;
  return new RegExp(
    `^${negative ? '-?' : ''}\\d*${decimal ? '(\\.\\d*)?' : ''}$`,
  );
}

const numericInputClasses = cn(
  'block w-full rounded border border-border-default bg-surface-card',
  'px-2 py-1 text-sm text-text-primary',
  'focus:outline-none focus:ring-2 focus:ring-brand-primary',
  // Hide native number-spinner arrows per CLAUDE.md visual rules
  'number-spinner-none',
);

export function VitalCell({
  entry,
  reading: readingIn,
  allReadings,
  compact = false,
  hideUnit = false,
  hearingVariant = 'pills',
  editing = false,
  formValues,
  onValueChange,
  lmpIsMenstrual,
  onLmpMenstrualChange,
  lmpShowIndicators = false,
  lmpIsBreastFeeding = false,
  onLmpBreastFeedingChange,
  lmpIsPregnant = false,
  onLmpPregnantChange,
  historicalReadings,
}: VitalCellProps) {
  // BMI and the waist-to-hip ratio: live from what is typed while editing, derived at rest (vitalsComputedLive); else as stored.
  const reading = computedVitalReading(entry.code, readingIn, allReadings, editing ? formValues : undefined);
  const Glyph = entry.icon;

  // BP is the only combo cell — render "<sys> / <dia>" when both are
  // present, "<sys> / —" when only sys is recorded, "—" when neither.
  const isComboBp = entry.code === VITAL_CODES.SYSTOLIC;
  const diastolicReading = isComboBp
    ? findReading(allReadings, VITAL_CODES.DIASTOLIC)
    : null;

  const primary = formatPrimaryValue(reading);
  const secondary = isComboBp ? formatPrimaryValue(diastolicReading) : null;
  const hasValue = vitalCellHasValue(entry, reading, allReadings);

  // Abnormal indicator — Flutter-parity replacement of the generic
  // "Abnormal" pill (see screenshots compared 2026-05-15). The pill
  // renders only for vitals with a clinical `normalValue` in the
  // catalog (Temp, Pulse, BP, RR, O₂ Sat, W/H Ratio), shows green
  // "NI" when normal, red "X% ↑/↓" or "Low" / "High" when abnormal.
  // Vitals with no clinical range (Height, Weight, Hip/Waist, Bioage,
  // Visual, Hearing, LMP, BMI) never show a pill, matching Flutter.
  // See abnormal-indicator.tsx for the full mapping.
  const indicatorProps = {
    reading,
    partnerReading: diastolicReading,
    hasNormalRange: entry.normalValue != null,
    belowLabel: entry.code === VITAL_CODES.O2_SAT ? 'Low' : undefined,
    aboveLabel: entry.code === VITAL_CODES.WAIST_TO_HIP_RATIO ? 'High' : undefined,
  };
  const abnormalTone = computeVitalAbnormalTone(indicatorProps);
  const isAbnormal = abnormalTone === 'abnormal';

  // Edit-mode plumbing.
  const showInput =
    editing && (entry.editor === 'numeric' || entry.editor === 'combo');
  const showLmpEditor = editing && entry.editor === 'lmp';
  const showHearingEditor = editing && entry.editor === 'hearing';
  const showVisualEditor = editing && entry.editor === 'visual';
  const isInputDecimal = entry.allowDecimal !== false;

  // Read displays use dedicated components for Hearing (color-coded
  // pills) and Visual Acuity (4-slash summary as two fractions).
  const isHearing = entry.editor === 'hearing';
  const isVisual = entry.editor === 'visual';

  const handleNumericChange =
    (code: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Whitelist by pattern — preserves cursor / paste behavior unlike
      // type="number" which rejects partial decimals like "36." mid-typing.
      if (raw === '' || inputPattern(entry).test(raw)) {
        onValueChange?.(code, raw);
      }
    };

  // Read-mode value display — picks the right component for special
  // cells (Hearing chips, Visual fractions, BP combo) or falls back
  // to the plain value + unit text. Used by the horizontal read
  // layout below.
  const readModeValue = (() => {
    if (isHearing) {
      return <HearingTestReadout value={reading?.quantity?.value} />;
    }
    if (isVisual) {
      return <VisualAcuityReadout value={reading?.quantity?.value} />;
    }
    return (
      <span
        className={cn(
          'text-base font-semibold',
          hasValue ? 'text-text-primary' : 'text-text-hint',
        )}
      >
        {isComboBp ? (
          <>
            {primary}
            <span className="mx-1 text-text-muted">/</span>
            {secondary}
          </>
        ) : (
          primary
        )}
        {entry.unit && !hideUnit ? (
          <span className="ml-1 text-xs font-normal text-text-muted">
            {entry.unit}
          </span>
        ) : null}
      </span>
    );
  })();

  // Flutter-parity HORIZONTAL read-mode layout (Andrew screenshot
  // comparison 2026-05-15):
  //   [icon] [value + pill]                 [sparkline]
  //          [label]
  // Edit mode preserves the existing vertical layout because the
  // inline numeric / date / dropdown editors need the room.
  if (!editing && compact) {
    // PRE-ENCOUNTER read tile = the published Flutter tile (PROD-1549, Ahmed 2026-09-05), measured
    // on dev-provider at 1920 / zoom 100 % / DPR 1: 321×61 incl. a 1px border, radius-lg, a BARE
    // 20px Scrips glyph 14px in (no tinted chip), text column 43px in with the VALUE (15px, cap
    // 11px below the top) over the LABEL (13px muted, 39px below the top), and a 45×2 trend rule
    // in the brand colour 8px from the right edge — Flutter draws that rule even with no history,
    // so the resting tile shows it; a real series replaces it with the sparkline.
    const series =
      entry.showGraph !== false && historicalReadings
        ? entry.code === VITAL_CODES.BMI
          ? extractBmiSeries(historicalReadings)
          : extractVitalSeries(entry.code, historicalReadings)
        : [];
    const compactValue =
      isHearing ? (
        <HearingTestReadout value={reading?.quantity?.value} />
      ) : isVisual ? (
        <VisualAcuityReadout value={reading?.quantity?.value} />
      ) : (
        // Pre-encounter (design rules, 15 Oct): an empty tile reads one quiet "—" (no unit fragments such as "—°C" or
        // "—/—"); with a value the unit is a meta-role span beside it. Look in showcase-bridge.css `/* pre-encounter */`.
        <span
          className="tabular-nums text-text-primary"
          data-slot="vital-value"
          data-empty={hasValue ? undefined : 'true'}
        >
          {!hasValue ? (
            '—'
          ) : isComboBp ? (
            <>
              {primary}
              <span className="mx-1">/</span>
              {secondary}
            </>
          ) : (
            primary
          )}
          {hasValue && entry.unit && !hideUnit ? (
            <span data-slot="vital-unit">
              {entry.unit}
            </span>
          ) : null}
        </span>
      );
    return (
      <div
        // The pre-encounter tile on the prototype's anatomy (15 Oct, the canvas pass): a label row (a neutral 16 glyph and
        // the label, meta) over a value row (the value in body strong, the abnormal indicator, the trend at the right).
        // One height for every tile (a label never wraps: it truncates), the one card, no coloured glyph. Look in
        // showcase-bridge.css `/* encounter canvas: reason + vitals */`.
        className="flex flex-col rounded-lg border border-border-default bg-surface-card"
        data-vital-code={entry.code}
        data-vital-abnormal={isAbnormal ? 'true' : 'false'}
      >
        <div className="flex min-w-0 items-center" data-slot="vital-label">
          <Glyph className="shrink-0" aria-hidden />
          {/* The FULL name (Hip Circumference, Waist-to-Hip Ratio, Visual Acuity…), "Temp" for Temperature. */}
          <p className="m-0 min-w-0 truncate">{entry.shortLabel === 'Temp' ? 'Temp' : entry.label}</p>
        </div>
        <div className="flex min-w-0 items-center" data-slot="vital-row">
          {compactValue}
          <VitalAbnormalIndicator {...indicatorProps} />
          <div className="ml-auto flex shrink-0 items-center justify-end" data-slot="vital-trend" aria-hidden>
            <Sparkline
              data={series}
              width={46}
              height={16}
              strokeWidth={1.5}
              inset={{ x: 0, y: 1 }}
              noTrend="baseline"
            />
          </div>
        </div>
      </div>
    );
  }

  if (!editing) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 rounded-md border border-border-default bg-surface-card px-3 py-2.5',
        )}
        data-vital-code={entry.code}
        data-vital-abnormal={isAbnormal ? 'true' : 'false'}
      >
        {/* Left — icon */}
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-blue-100">
          <Glyph className="size-4 text-text-secondary" aria-hidden />
        </div>

        {/* Middle — value + inline pill, label below */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            {readModeValue}
            <VitalAbnormalIndicator {...indicatorProps} />
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {entry.shortLabel ?? entry.label}
          </p>
        </div>

        {/* Right — sparkline (Flutter parity
            `fat_vital_cell_widget.dart:253-263`). Renders only when
            the catalog opts in AND we have ≥2 historical points.
            Reserved space stays consistent across rows so cells line
            up vertically even when some have no graph data. */}
        <div className="flex h-5 w-15 flex-shrink-0 items-center justify-end">
          <Sparkline
            data={
              entry.showGraph !== false && historicalReadings
                ? entry.code === VITAL_CODES.BMI
                  ? extractBmiSeries(historicalReadings)
                  : extractVitalSeries(entry.code, historicalReadings)
                : []
            }
            width={60}
            height={20}
            noTrend="hide"
          />
        </div>
      </div>
    );
  }

  // Edit mode — preserved vertical layout with inputs.
  return (
    <div
      className={cn(
        'relative flex flex-col rounded-lg border bg-surface-card px-3 py-2.5',
        isAbnormal ? 'border-red-300' : 'border-border-default',
      )}
      data-vital-code={entry.code}
      data-vital-abnormal={isAbnormal ? 'true' : 'false'}
    >
      <div className="absolute right-2 top-2">
        <VitalAbnormalIndicator {...indicatorProps} />
      </div>

      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-grey-100">
        <Glyph className="size-4 text-text-secondary" aria-hidden />
      </div>

      <p className="mt-2 text-xs font-medium text-text-secondary">
        {entry.label}
      </p>

      {/* Body — read OR edit */}
      {showLmpEditor ? (
        <LmpEditor
          showIndicators={lmpShowIndicators}
          isBreastFeeding={lmpIsBreastFeeding}
          onBreastFeedingChange={onLmpBreastFeedingChange}
          isPregnant={lmpIsPregnant}
          onPregnantChange={onLmpPregnantChange}
          value={formValues?.[entry.code] ?? ''}
          isMenstrual={lmpIsMenstrual ?? true}
          onValueChange={(next) => onValueChange?.(entry.code, next)}
          onMenstrualChange={(next) => onLmpMenstrualChange?.(next)}
        />
      ) : showHearingEditor ? (
        (() => {
          const { left, right } = parseHearingValue(
            formValues?.[entry.code] ?? '',
          );
          return (
            <HearingTestEditor variant={hearingVariant}
              left={left}
              right={right}
              onChange={(nextL, nextR) =>
                onValueChange?.(entry.code, combineHearingValue(nextL, nextR) ?? '')
              }
            />
          );
        })()
      ) : showVisualEditor ? (
        <VisualAcuityEditor
          value={formValues?.[entry.code] ?? ''}
          onChange={(next) => onValueChange?.(entry.code, next)}
          numerators={ALLOWED_NUMERATOR}
          denominators={ALLOWED_DENOMINATOR}
        />
      ) : showInput ? (
        entry.editor === 'combo' ? (
          // BP — two numeric inputs separated by `/`.
          <div className="mt-1 flex items-center gap-1">
            <input
              type="text"
              inputMode={isInputDecimal ? 'decimal' : 'numeric'}
              aria-label={`${entry.label} (systolic)`}
              maxLength={entry.maxDigits}
              value={formValues?.[entry.code] ?? ''}
              onChange={handleNumericChange(entry.code)}
              className={cn(numericInputClasses, compact && 'w-14 flex-none')}
              placeholder="—"
            />
            <span className="text-text-muted">/</span>
            <input
              type="text"
              inputMode={isInputDecimal ? 'decimal' : 'numeric'}
              aria-label={`${entry.label} (diastolic)`}
              maxLength={entry.maxDigits}
              value={formValues?.[entry.combinesWith as string] ?? ''}
              onChange={handleNumericChange(entry.combinesWith as string)}
              className={cn(numericInputClasses, compact && 'w-14 flex-none')}
              placeholder="—"
            />
            {entry.unit && !hideUnit ? (
              <span className="text-xs text-text-muted whitespace-nowrap">
                {entry.unit}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-1">
            <input
              type="text"
              inputMode={isInputDecimal ? 'decimal' : 'numeric'}
              aria-label={entry.label}
              maxLength={entry.maxDigits}
              value={formValues?.[entry.code] ?? ''}
              onChange={handleNumericChange(entry.code)}
              className={cn(numericInputClasses, compact && 'w-14 flex-none')}
              placeholder="—"
            />
            {entry.unit && !hideUnit ? (
              <span className="text-xs text-text-muted whitespace-nowrap">
                {entry.unit}
              </span>
            ) : null}
          </div>
        )
      ) : isHearing ? (
        // Read mode for Hearing — colored chips.
        <div className="mt-1">
          <HearingTestReadout value={reading?.quantity?.value} />
        </div>
      ) : isVisual ? (
        // Read mode for Visual Acuity — two fractions, OS / OD.
        <div className="mt-1">
          <VisualAcuityReadout value={reading?.quantity?.value} />
        </div>
      ) : (
        // Read mode (or non-editable cell in edit mode).
        <p className="mt-1 flex items-baseline gap-1">
          <span
            className={cn(
              'text-md font-semibold',
              hasValue ? 'text-text-primary' : 'text-text-hint',
            )}
          >
            {isComboBp ? (
              <>
                {primary}
                <span className="mx-1 text-text-muted">/</span>
                {secondary}
              </>
            ) : (
              primary
            )}
          </span>
          {entry.unit && !hideUnit ? (
            <span className="text-xs text-text-muted">{entry.unit}</span>
          ) : null}
        </p>
      )}

      {entry.isComputed ? (
        <p className="mt-1 text-xs text-text-hint">Computed</p>
      ) : null}
    </div>
  );
}
